'use client';

import { useEffect, useRef } from 'react';

const problems = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </svg>
    ),
    label: 'Idee',
    title: 'Die Idee ist da – der erste Schritt fehlt',
    desc: 'Du trägst eine Geschäftsidee in dir. Aber wie du wirklich anfängst, bleibt unklar.',
    color: 'bg-sage-50',
    accent: 'text-sage',
    border: 'border-sage/20',
    size: 'md:col-span-2',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12h18" />
        <path d="M12 3l9 9-9 9" />
      </svg>
    ),
    label: 'Bürokratie',
    title: 'Bürokratie-Dschungel',
    desc: 'Gewerbeanmeldung, Finanzamt, Rechtsform, Steuern – das deutsche System überfordert dich.',
    color: 'bg-sand-light/30',
    accent: 'text-sand-dark',
    border: 'border-sand/30',
    size: '',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <path d="M14 17h7M17 14v7" />
      </svg>
    ),
    label: 'Struktur',
    title: 'Kein Plan, keine Struktur',
    desc: 'Du brauchst einen Businessplan, eine Strategie, einen roten Faden. Aber wo anfangen?',
    color: 'bg-slate-blue/5',
    accent: 'text-slate-blue',
    border: 'border-slate-blue/20',
    size: '',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    label: 'Unsicherheit',
    title: 'Wird sich meine Idee tragen?',
    desc: 'Funktioniert meine Idee am Markt? Diese Frage hält dich nachts wach.',
    color: 'bg-sage-50',
    accent: 'text-sage',
    border: 'border-sage/20',
    size: '',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" />
      </svg>
    ),
    label: 'Freiheit',
    title: 'Raus aus der Abhängigkeit',
    desc: 'Kein Chef, keine Vorgaben – dein eigenes Ding. Du spürst, dass es möglich ist.',
    color: 'bg-sand-light/30',
    accent: 'text-sand-dark',
    border: 'border-sand/30',
    size: 'md:col-span-2',
  },
];

export default function ProblemSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    const elements = sectionRef?.current?.querySelectorAll('.reveal-hidden');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 px-6 bg-warm-white" id="probleme">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="badge-sage rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest inline-block mb-5">
            Erkennst du dich wieder?
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal leading-tight">
            Du willst gründen – aber etwas hält dich{' '}
            <span className="italic text-sage">zurück?</span>
          </h2>
          <p className="mt-4 text-muted text-lg max-w-xl mx-auto font-light leading-relaxed">
            Die meisten angehenden Gründer kennen dieses Gefühl.
            Dir fehlt nicht die Fähigkeit – dir fehlt nur Klarheit, Struktur
            und jemand der den Weg kennt.
          </p>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {problems?.map((p, i) => (
            <div
              key={i}
              className={`bento-problem reveal-hidden delay-${(i % 4 + 1) * 100} ${p?.size} ${p?.color} border ${p?.border} rounded-3xl p-7 transition-all duration-500 cursor-default`}
            >
              <div className={`${p?.accent} mb-4`}>{p?.icon}</div>
              <span className={`text-xs font-bold uppercase tracking-widest ${p?.accent} mb-2 block`}>
                {p?.label}
              </span>
              <h3 className="text-lg font-semibold text-charcoal mb-2 leading-snug">
                {p?.title}
              </h3>
              <p className="text-muted text-sm leading-relaxed font-light">
                {p?.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom pull quote */}
        <div className="mt-16 text-center reveal-hidden delay-300">
          <p className="font-serif text-2xl md:text-3xl italic text-charcoal/70 font-light max-w-2xl mx-auto leading-relaxed">
            "Das Problem ist nicht, dass deine Idee nicht gut genug ist –
            <br className="hidden md:block" />
            das Problem ist, dass dir noch der Plan fehlt, sie umzusetzen."
          </p>
          <span className="mt-4 block text-sm text-muted font-medium">— Andrej Martens, Ikiguide</span>
        </div>
      </div>
    </section>
  );
}